/**
 * Monitoring Safe Wrapper - Safe Proxy for Monitoring Service
 *
 * Provides a fail-safe wrapper around the monitoring service that:
 * - Handles dynamic import failures gracefully
 * - Provides fallback no-op methods when monitoring is unavailable
 * - Prevents "monitoring is not defined" errors in production
 * - Logs warnings instead of throwing errors
 * - Uses Proxy pattern for transparent method access
 *
 * This wrapper solves the production issue where:
 * - Monitoring module fails to load (network error, CDN issue, build problem)
 * - Code attempts to call monitoring.trackError() on undefined
 * - ReferenceError is thrown, causing unhandled promise rejections
 * - Errors cascade and cannot be logged because monitoring is unavailable
 *
 * @module services/monitoring-safe
 * @see MONITORING-SAFE-IMPLEMENTATION-GUIDE.md
 */

/**
 * Stores the dynamically loaded monitoring instance
 * @type {Object|null}
 */
let monitoringInstance = null;

/**
 * Flag indicating if monitoring has been successfully initialized
 * @type {boolean}
 */
let monitoringAvailable = false;

/**
 * Initialize the monitoring service by dynamically importing it
 *
 * This function attempts to load the monitoring module and handles failures gracefully.
 * If the import fails (e.g., network error, build issue), it logs the error and returns false,
 * allowing the application to continue without monitoring.
 *
 * @async
 * @returns {Promise<boolean>} True if monitoring was successfully loaded, false otherwise
 *
 * @example
 * const success = await initializeMonitoring();
 * if (success) {
 *   console.log('Monitoring available');
 * } else {
 *   console.log('Monitoring unavailable, using fallback');
 * }
 */
export async function initializeMonitoring() {
    try {
        // eslint-disable-next-line no-console -- Intentional: initialization status logging
        console.log('🔄 Loading monitoring module...');

        // Dynamically import the monitoring module
        const monitoringModule = await import('./monitoring.js');

        // Extract the monitoring export (could be default or named export)
        monitoringInstance = monitoringModule.monitoring || monitoringModule.default;

        // Verify we got a valid monitoring object
        if (!monitoringInstance || typeof monitoringInstance !== 'object') {
            throw new Error('Monitoring module did not export a valid monitoring object');
        }

        monitoringAvailable = true;
        // eslint-disable-next-line no-console -- Intentional: initialization success logging
        console.log('✅ Monitoring module loaded successfully');
        return true;

    } catch (error) {
        // eslint-disable-next-line no-console -- Intentional: error reporting for monitoring failure
        console.error('❌ Failed to load monitoring module:', error);
        // eslint-disable-next-line no-console -- Intentional: warning about degraded functionality
        console.warn('⚠️ Application will continue without monitoring. Errors will not be tracked.');

        // Set to null to ensure fallback behavior
        monitoringInstance = null;
        monitoringAvailable = false;

        return false;
    }
}

/**
 * Safe monitoring proxy that prevents errors when monitoring is unavailable
 *
 * This Proxy wraps the monitoring instance and provides several safety features:
 * 1. Checks if monitoring is initialized before each method call
 * 2. Logs a warning if monitoring is not available (only once per method)
 * 3. Returns a no-op function for any unavailable methods
 * 4. Wraps all method calls in try-catch blocks to prevent errors from escaping
 * 5. Handles edge cases where monitoringInstance is null or undefined
 *
 * The Proxy is transparent - code can call safeMonitoring.trackError() exactly
 * like it would call monitoring.trackError(), but without risking ReferenceErrors.
 *
 * @type {Proxy}
 *
 * @example
 * // Safe - never throws ReferenceError even if monitoring unavailable
 * safeMonitoring.trackError(new Error('test'));
 * safeMonitoring.trackEvent('user_action');
 *
 * @example
 * // Works correctly when monitoring is available
 * await initializeMonitoring();
 * safeMonitoring.initialize(config); // Calls real monitoring.initialize()
 */
export const safeMonitoring = new Proxy({}, {
    /**
     * Proxy get handler that intercepts property access
 *
     * @param {Object} target - The proxy target (empty object)
     * @param {string} property - The property being accessed (e.g., 'trackError')
     * @returns {Function} The monitoring method or a safe wrapper
     */
    get(target, property) {
        // Check if monitoring is available and initialized
        if (!monitoringAvailable || !monitoringInstance) {
            // Return a no-op function that logs a warning (only once per method)
            return function safeFallbackMethod() {
                // Only log warning once per method to avoid console spam
                if (!safeFallbackMethod._warningLogged) {
                    // eslint-disable-next-line no-console -- Intentional: warning when monitoring unavailable
                    console.warn(`⚠️ Monitoring not available: ${property}() called but monitoring is not initialized`);
                    safeFallbackMethod._warningLogged = true;
                }
                // Return nothing - no-op
            };
        }

        // Get the actual monitoring method
        const monitoringMethod = monitoringInstance[property];

        // If method doesn't exist on monitoring instance, return no-op
        if (typeof monitoringMethod !== 'function') {
            return function safeUndefinedMethod() {
                // eslint-disable-next-line no-console -- Intentional: warning for undefined method
                console.warn(`⚠️ Monitoring method not found: ${property}()`);
            };
        }

        // Return a safe wrapper around the monitoring method
        return function safeMonitoringMethod(...args) {
            try {
                // Call the actual monitoring method with correct context (this binding)
                return monitoringMethod.apply(monitoringInstance, args);
            } catch (error) {
                // Catch and log any errors from the monitoring method
                // This prevents monitoring errors from breaking the application
                // eslint-disable-next-line no-console -- Intentional: error reporting for monitoring failures
                console.error(`❌ Error in monitoring.${property}():`, error);

                // Log the error details for debugging
                if (args.length > 0) {
                    // eslint-disable-next-line no-console -- Intentional: debugging information
                    console.error(`   Arguments:`, args);
                }

                return undefined;
            }
        };
    }
});

/**
 * Check if monitoring is currently available
 *
 * Useful for conditional monitoring logic where different behavior
 * is needed based on monitoring availability.
 *
 * @returns {boolean} True if monitoring is available and initialized
 *
 * @example
 * if (isMonitoringAvailable()) {
 *   // Do expensive tracking operation
 *   safeMonitoring.trackEvent('complex_event', computeExpensiveData());
 * } else {
 *   // Skip tracking to avoid wasted computation
 * }
 */
export function isMonitoringAvailable() {
    return monitoringAvailable && monitoringInstance !== null;
}

/**
 * Get the underlying monitoring instance (for advanced use cases)
 *
 * ⚠️ WARNING: Direct access to the monitoring instance bypasses all safety features.
 * Only use this if you need access to Application Insights SDK methods that are not
 * exposed through the standard monitoring interface.
 *
 * Always check if monitoring is available before using the instance.
 *
 * @returns {Object|null} The monitoring instance or null if unavailable
 *
 * @example
 * const instance = getMonitoringInstance();
 * if (instance && instance.appInsights) {
 *   // Access Application Insights SDK directly
 *   instance.appInsights.context.application.ver = '1.2.3';
 * }
 */
export function getMonitoringInstance() {
    return monitoringInstance;
}

/**
 * ErrorSeverity enum values (available after monitoring is loaded)
 * Re-exported for convenience
 */
export const ErrorSeverity = {
    Critical: 4,
    Error: 3,
    Warning: 2,
    Information: 1,
    Verbose: 0
};

/**
 * ErrorCategory enum values (available after monitoring is loaded)
 * Re-exported for convenience
 */
export const ErrorCategory = {
    Authentication: 'Authentication',
    Network: 'Network',
    Validation: 'Validation',
    Business: 'Business',
    System: 'System',
    Unknown: 'Unknown'
};
